import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { rpc, RpcError } from "./rpc";

/**
 * The public share surface (`docs/spec/shares.md` §3.3, §4.2). Every call here is made
 * without credentials: the link in the address bar is the only thing authorizing it.
 */

/** A node of `data.sharedTree`. `url` is an **internal** path; the client maps it. */
export interface SharedTreeNode {
  id: string;
  url: string;
  title: string;
  children: SharedTreeNode[];
}

export interface PublicShare {
  id: string;
  urlId: string | null;
  url: string;
  documentId: string | null;
  documentTitle: string | null;
  documentUrl: string | null;
  sourceTitle: string | null;
  sourcePath: string | null;
  collectionId: string | null;
  published: boolean;
  includeChildDocuments: boolean;
  allowIndexing: boolean;
  allowSubscriptions: boolean;
  showLastUpdated: boolean;
  showTOC: boolean;
  title: string | null;
  iconUrl: string | null;
  domain: string | null;
  views: number;
  createdAt: string;
  updatedAt: string;
}

/** The four branding keys a public page gets, and nothing else about the workspace. */
export interface PublicShareTeam {
  name: string;
  avatarUrl: string | null;
  customTheme: { accent?: string; accentText?: string } | null;
  tocPosition: "left" | "right";
}

export interface PublicCollection {
  id: string;
  url: string;
  urlId: string;
  name: string;
  description?: string | null;
  icon: string | null;
  color: string | null;
}

/** The public presentation of a document: no placement, no identity, no analytics. */
export interface PublicDocument {
  id: string;
  url: string;
  urlId: string;
  title: string;
  text?: string;
  data?: Record<string, unknown>;
  icon: string | null;
  color: string | null;
  tasks: { completed: number; total: number };
  createdAt: string;
  /** Present only when the share's `showLastUpdated` is on. */
  updatedAt?: string;
  publishedAt: string | null;
  fullWidth: boolean;
  revision: number;
}

export interface ShareInfo {
  shares: PublicShare[];
  sharedTree: SharedTreeNode | null;
  team: PublicShareTeam;
  collection: PublicCollection | null;
  document: PublicDocument | null;
}

export interface SearchResultItem {
  context?: string;
  ranking?: number;
  document: PublicDocument & { summary?: string };
}

export const shareKeys = {
  info: (shareId: string) => ["shares.info", shareId] as const,
  document: (shareId: string, documentId: string) =>
    ["documents.info", shareId, documentId] as const,
  search: (shareId: string, query: string) => ["documents.search", shareId, query] as const,
};

/** `not_found` is the answer for a wrong link, an unpublished share and a revoked one alike. */
export function isMissing(error: unknown): boolean {
  return error instanceof RpcError && error.id === "not_found";
}

export function useShareInfo(shareId: string): UseQueryResult<ShareInfo> {
  return useQuery({
    queryKey: shareKeys.info(shareId),
    queryFn: async () => (await rpc<ShareInfo>("shares.info", { id: shareId })).data,
    enabled: shareId !== "",
    retry: false,
  });
}

/**
 * One document inside the share. `documentId` may be a uuid or the `urlId` tail of a slug,
 * which is what makes `/s/<shareId>/doc/<slug>-<urlId>` resolvable straight from the URL.
 */
export function useShareDocument(
  shareId: string,
  documentId: string,
): UseQueryResult<PublicDocument> {
  return useQuery({
    queryKey: shareKeys.document(shareId, documentId),
    queryFn: async () =>
      (await rpc<PublicDocument>("documents.info", { shareId, id: documentId })).data,
    enabled: shareId !== "" && documentId !== "",
    retry: false,
  });
}

export function useShareSearch(shareId: string, query: string): UseQueryResult<SearchResultItem[]> {
  return useQuery({
    queryKey: shareKeys.search(shareId, query),
    queryFn: async () =>
      (await rpc<SearchResultItem[]>("documents.search", { shareId, query, limit: 25 })).data,
    enabled: shareId !== "" && query.trim() !== "",
    retry: false,
  });
}
